import React, { ReactNode } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { adminColors } from '../../styles/adminStyles';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 5, 10, 0.85);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 0;
  overflow-y: auto;
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled.div`
  background: ${adminColors.surface};
  border: 1px solid ${adminColors.border};
  width: min(960px, 92vw);
  margin: 0 24px;
  border-radius: 12px;
  box-shadow:
    0 0 40px rgba(0, 212, 255, 0.15),
    0 20px 60px rgba(0, 0, 0, 0.5);
  position: relative;
  overflow: hidden;
  animation: ${slideIn} 0.3s ease;
`;

const ModalHeader = styled.div`
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.12) 0%, rgba(0, 164, 255, 0.06) 100%);
  padding: 18px 24px;
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.3rem;
  font-weight: 600;
  color: ${adminColors.text};
  text-transform: uppercase;
  letter-spacing: 2px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${adminColors.borderLight};
`;

const CloseButton = styled.button`
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  color: ${adminColors.textMuted};
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 4px 10px;

  &:hover {
    color: ${adminColors.primary};
    border-color: ${adminColors.borderLight};
    background: rgba(0, 212, 255, 0.05);
  }
`;

const ModalContent = styled.div`
  padding: 24px;
  padding-bottom: 56px;
  max-height: calc(100vh - 180px);
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
  }

  &::-webkit-scrollbar-thumb {
    background: ${adminColors.borderLight};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${adminColors.border};
  }
`;

const StatusLine = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 28px;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.08) 0%, rgba(0, 164, 255, 0.04) 100%);
  border-top: 1px solid ${adminColors.borderMuted};
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Courier New', monospace;
  font-size: 10px;
  color: ${adminColors.textMuted};
  letter-spacing: 1px;
  text-transform: uppercase;
`;

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const sessionId = `MOD-${Math.floor(Math.random() * 1000000)}`;

  return ReactDOM.createPortal(
    <Overlay onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <div>{title}</div>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>
        <ModalContent>
          {children}
        </ModalContent>
        <StatusLine>
          SESSION: {sessionId} | SECURE PROTOCOL ACTIVE
        </StatusLine>
      </ModalContainer>
    </Overlay>,
    document.body
  );
};

export default Modal;
